import { useEffect, useRef } from 'react';

import { useLocation } from 'react-router-dom';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import { responsiveFontSizes, styled } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import type {} from '@mui/material/themeCssVarsAugmentation';
import Typography from '@mui/material/Typography';

import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import MobiledataOffIcon from '@mui/icons-material/MobiledataOff';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';

import dayjs from 'dayjs';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { logAnalyticsEvent, setAnalyticsEnvironment, setAnalyticsVersion, startPerfTrace } from './analytics/analytics';
import { usePageTracking } from './analytics/usePageTracking';
import GratiAppBar from './components/common/GratiAppBar';
import GratiNavRail from './components/common/GratiNavRail';
import GratiNoticeBanner from './components/common/GratiNoticeBanner';
import Hourglass from './components/common/Hourglass';
import { useApp } from './contextProviders/AppProvider';
import { useData } from './contextProviders/DataProvider';
import { MapsProvider } from './contextProviders/MapsProvider';
import useActionNotices from './dataHooks/useActionNotices';
import RouteTable from './routes/RouteTable';
import { useVersionCheck } from './stateHooks/useVersionCheck';
import mui6Theme from './styles/mui6Theme';
import { useOnlineStatus } from './utils/useOnlineStatus';

setAnalyticsVersion(__APP_VERSION__);
setAnalyticsEnvironment();

function App() {
  const navRailWidth = '5rem';
  usePageTracking();
  const authTrace = useRef(startPerfTrace('auth_resolution'));
  const { isEmulator, isOnline } = useOnlineStatus();
  const { isHomePage } = useApp();
  const { userState } = useData();

  useEffect(() => {
    if (!authTrace.current) return;
    if (userState.status === 'ready' || userState.status === 'logged_out') {
      authTrace.current.putAttribute('auth_result', userState.status);
      authTrace.current.stop();
      authTrace.current = null;
    }
  }, [userState.status]);

  useEffect(() => {
    if (userState.status !== 'ready' && userState.status !== 'logged_out') return;
    const entries = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
    const scripts = entries.filter(e => e.initiatorType === 'script');
    const transferred = scripts.reduce((n, e) => n + e.transferSize, 0);
    const cached = scripts.filter(e => e.transferSize === 0 && e.decodedBodySize > 0).length;
    logAnalyticsEvent('js_load_summary', {
      transferred_kb: Math.round(transferred / 1024),
      cached_chunks: cached,
      total_chunks: scripts.length,
      auth_result: userState.status,
    });
  }, [userState.status]);
  const { actionNotices } = useActionNotices();
  const location = useLocation();
  useVersionCheck(location.pathname);
  const isPublicPage = location.pathname.startsWith('/public/') || location.pathname.startsWith('/pages/');
  const showAppBar = !(isPublicPage && userState.status === 'logged_out');

  dayjs.extend(utc);
  dayjs.extend(timezone);

  const responsiveMui6Theme = responsiveFontSizes(mui6Theme);
  const Offset = styled('div')(({ theme }) => theme.mixins.toolbar);

  // App pre-render until we are in a definitive state of logged in or logged out.
  if (!['ready', 'logged_out'].includes(userState.status)) {
    return (
      <MapsProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeProvider theme={responsiveMui6Theme} defaultMode="system">
            <Box sx={{ display: 'flex', flexGrow: 1 }}>
              <GratiAppBar
                isTopLevelPage={isHomePage}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
              />
              <Stack
                className="AppStack"
                sx={{
                  position: "sticky",
                  display: 'flex',
                  flexGrow: 1,
                  spacing: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: { xs: '100vw', md: `calc(100vw - ${navRailWidth})` },
                  backgroundColor: 'surface.main'
                }}>
                <Offset />
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <Hourglass />
                  <Typography variant="bodyLarge" sx={{
                    color: "text.secondary"
                  }}>
                    {userState.status === 'authenticating_user' && 'Driving Range...'}
                    {userState.status === 'authenticating_profile' && 'Pitching and Chipping...'}
                    {userState.status === 'authenticating_memberships' && 'Putting...'}
                    {userState.status === 'unknown' && 'Stretching...'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </ThemeProvider>
        </LocalizationProvider>
      </MapsProvider>
    );
  }

  return (
    <MapsProvider>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ThemeProvider theme={responsiveMui6Theme} defaultMode="system">
          <Box sx={{ display: 'flex', flexGrow: 1 }}>
            {showAppBar && (
              <GratiAppBar
                isTopLevelPage={isHomePage}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
              />
            )}
            {userState.status === 'ready' && <GratiNavRail sx={{ display: { xs: 'none', md: 'flex' } }} />}
            <Stack
              className="AppStack"
              sx={{
                position: "sticky",
                display: 'flex',
                flexGrow: 1,
                spacing: 1,
                justifyContent: 'flex-start',
                width: { xs: '100vw', md: `calc(100vw - ${navRailWidth})` },
                backgroundColor: 'surface.main'
              }}>
              {showAppBar && <Offset />}
              <Stack  sx={{ p: 1 }}>
                {userState.status === 'ready' && (isHomePage || actionNotices.length > 0) && (
                  <GratiNoticeBanner
                    actionNotices={actionNotices}
                    sx={{
                      height: '4rem',
                      display: 'flex',
                      alignItems: 'center',
                      backgroundColor: 'primary.container',
                    }}
                  />
                )}
                <RouteTable />
                {(!isOnline || isEmulator) && (
                  <Stack
                    direction="row"
                    spacing={1}
                    divider={<Divider orientation="vertical" flexItem />}
                    sx={{
                      display: 'flex',
                      flexGrow: 1,
                      px: 2,
                      backgroundColor: 'surface.main',
                      color: 'surface.contrastText',
                    }}
                  >
                    {isOnline ? (
                      <SignalWifi4BarIcon sx={{ color: 'success.main' }} />
                    ) : (
                      <MobiledataOffIcon sx={{ color: 'error.main' }} />
                    )}
                    <Typography variant="labelMedium">
                      {isOnline ? 'Online' : 'Offline'}
                    </Typography>
                    {isEmulator ? (
                      <DeveloperModeIcon sx={{ color: 'success.main' }} />
                    ) : (
                      <ImportExportIcon sx={{ color: 'warning.main' }} />
                    )}
                    <Typography variant="labelMedium" component="span">
                      {isEmulator ? 'Emulator' : 'Production'}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Box>
        </ThemeProvider>
      </LocalizationProvider>
    </MapsProvider>
  );
}

export default App;
